/**
 * Ways: the roads, tracks, paths and streams of a world, from OpenStreetMap (docs/features/play-world-osm.md §3).
 *
 * A world holds them as polylines in its own frame: metres east and north of the area's centre, the frame the
 * surface, the course and the markers use. In a world pack they are metres east and north of the south-west corner in
 * whole metres, each point a zigzag varint step from the one before (the first from the previous way's last point),
 * and each way a class byte and a point count. This module has no dependencies, so the viewer's pack reader stays
 * small; reading OpenStreetMap is `osm.ts`.
 */

export const WAY_CLASSES = ['major', 'minor', 'service', 'track', 'path', 'stream'] as const
export type WayClass = (typeof WAY_CLASSES)[number]

/** Codes 0–5 index `WAY_CLASSES`. */
export const WAY_CODE: Record<WayClass, number> = Object.fromEntries(WAY_CLASSES.map((name, index) => [name, index])) as Record<WayClass, number>

/** Paths and tracks: what a trail course runs on. */
export const TRAIL_CLASSES: readonly WayClass[] = ['track', 'path']

/** Dropped first, in this order, when the ways would outgrow their budget; the rest are always kept. */
export const DROPPABLE_CLASSES: readonly WayClass[] = ['service', 'minor']

export interface WorldWays {
  /** One `WAY_CODE` per way. */
  classes: Uint8Array
  /** Index of each way's first point, and one past the last way's end: `classes.length + 1` entries. */
  starts: Uint32Array
  /** x, y per point: metres east and north of the area's centre. */
  points: Float32Array
}

/** One way while it is built: its class and its points as [x, y, x, y, …] in the area's frame. */
export interface WayLine {
  wayClass: number
  points: number[]
}

export function wayCount(ways: WorldWays): number {
  return ways.classes.length
}

export function waysFromLines(lines: readonly WayLine[]): WorldWays {
  const kept = lines.filter((line) => line.points.length >= 4)
  const classes = new Uint8Array(kept.length)
  const starts = new Uint32Array(kept.length + 1)
  let total = 0
  kept.forEach((line, index) => {
    classes[index] = line.wayClass
    starts[index] = total
    total += line.points.length / 2
  })
  starts[kept.length] = total
  const points = new Float32Array(total * 2)
  kept.forEach((line, index) => points.set(line.points, starts[index] * 2))
  return { classes, starts, points }
}

/** Every way's points as [x, y, …], in order. */
export function linesOf(ways: WorldWays): WayLine[] {
  return Array.from(ways.classes, (wayClass, index) => ({
    wayClass,
    points: Array.from(ways.points.subarray(ways.starts[index] * 2, ways.starts[index + 1] * 2)),
  }))
}

/** How many ways of each class. */
export function wayCounts(ways: WorldWays): Partial<Record<WayClass, number>> {
  const counts: Partial<Record<WayClass, number>> = {}
  for (const code of ways.classes) {
    const name = WAY_CLASSES[code]
    if (name) counts[name] = (counts[name] ?? 0) + 1
  }
  return counts
}

/** Length of each class in metres. */
export function wayLengths(ways: WorldWays): Partial<Record<WayClass, number>> {
  const lengths: Partial<Record<WayClass, number>> = {}
  for (let way = 0; way < ways.classes.length; way++) {
    const name = WAY_CLASSES[ways.classes[way]]
    let length = 0
    for (let point = ways.starts[way] + 1; point < ways.starts[way + 1]; point++) {
      length += Math.hypot(ways.points[point * 2] - ways.points[point * 2 - 2], ways.points[point * 2 + 1] - ways.points[point * 2 - 1])
    }
    if (name) lengths[name] = (lengths[name] ?? 0) + length
  }
  return lengths
}

/** Squared distance from p to the segment a–b. */
function segmentDistanceSquared(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax
  const dy = by - ay
  const lengthSquared = dx * dx + dy * dy
  const t = lengthSquared > 0 ? Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / lengthSquared)) : 0
  const ex = ax + t * dx - px
  const ey = ay + t * dy - py
  return ex * ex + ey * ey
}

/** Douglas–Peucker on [x, y, …]: every dropped point lies within `tolerance` metres of the line kept. */
export function simplifyLine(points: readonly number[], tolerance: number): number[] {
  const count = points.length / 2
  if (count <= 2) return points.slice()
  const keep = new Uint8Array(count)
  keep[0] = 1
  keep[count - 1] = 1
  const limit = tolerance * tolerance
  const stack: [number, number][] = [[0, count - 1]]
  while (stack.length) {
    const [first, last] = stack.pop()!
    let farthest = -1
    let farthestDistance = limit
    for (let index = first + 1; index < last; index++) {
      const distance = segmentDistanceSquared(points[index * 2], points[index * 2 + 1], points[first * 2], points[first * 2 + 1], points[last * 2], points[last * 2 + 1])
      if (distance > farthestDistance) {
        farthest = index
        farthestDistance = distance
      }
    }
    if (farthest < 0) continue
    keep[farthest] = 1
    stack.push([first, farthest], [farthest, last])
  }
  const out: number[] = []
  for (let index = 0; index < count; index++) if (keep[index]) out.push(points[index * 2], points[index * 2 + 1])
  return out
}

class ByteWriter {
  private bytes = new Uint8Array(1024)
  length = 0

  private room(extra: number) {
    if (this.length + extra <= this.bytes.length) return
    const grown = new Uint8Array(Math.max(this.bytes.length * 2, this.length + extra))
    grown.set(this.bytes.subarray(0, this.length))
    this.bytes = grown
  }

  varint(value: number) {
    this.room(5)
    let rest = value >>> 0
    while (rest >= 0x80) {
      this.bytes[this.length++] = (rest & 0x7f) | 0x80
      rest >>>= 7
    }
    this.bytes[this.length++] = rest
  }

  zigzag(value: number) {
    this.varint(((value << 1) ^ (value >> 31)) >>> 0)
  }

  result(): Uint8Array {
    return this.bytes.slice(0, this.length)
  }
}

export class WaysFormatError extends Error {}

/**
 * The ways as pack bytes. Points are rounded to whole metres from the south-west corner of an area `widthM` by
 * `heightM`; a way whose points round onto one spot is left out.
 */
export function encodeWays(ways: WorldWays, widthM: number, heightM: number): Uint8Array {
  const writer = new ByteWriter()
  const halfWidth = widthM / 2
  const halfHeight = heightM / 2
  let lastX = 0
  let lastY = 0
  const quantised: number[] = []
  const count = ways.classes.length
  // A count first, so a reader knows how many ways follow.
  const body = new ByteWriter()
  let written = 0
  for (let way = 0; way < count; way++) {
    quantised.length = 0
    for (let point = ways.starts[way]; point < ways.starts[way + 1]; point++) {
      const x = Math.round(ways.points[point * 2] + halfWidth)
      const y = Math.round(ways.points[point * 2 + 1] + halfHeight)
      const at = quantised.length
      if (at >= 2 && quantised[at - 2] === x && quantised[at - 1] === y) continue
      quantised.push(x, y)
    }
    if (quantised.length < 4) continue
    body.varint(ways.classes[way])
    body.varint(quantised.length / 2)
    for (let index = 0; index < quantised.length; index += 2) {
      body.zigzag(quantised[index] - lastX)
      body.zigzag(quantised[index + 1] - lastY)
      lastX = quantised[index]
      lastY = quantised[index + 1]
    }
    written++
  }
  writer.varint(written)
  const head = writer.result()
  const tail = body.result()
  const bytes = new Uint8Array(head.length + tail.length)
  bytes.set(head)
  bytes.set(tail, head.length)
  return bytes
}

export function decodeWays(bytes: Uint8Array, widthM: number, heightM: number): WorldWays {
  let offset = 0
  const varint = () => {
    let value = 0
    let shift = 0
    for (;;) {
      if (offset >= bytes.length) throw new WaysFormatError('World pack ways run past their section')
      const byte = bytes[offset++]
      value += (byte & 0x7f) * 2 ** shift
      if (byte < 0x80) return value
      shift += 7
      if (shift > 28) throw new WaysFormatError('World pack ways hold a number too large')
    }
  }
  const zigzag = () => {
    const value = varint()
    return value % 2 ? -(value + 1) / 2 : value / 2
  }
  const halfWidth = widthM / 2
  const halfHeight = heightM / 2
  const count = varint()
  const classes = new Uint8Array(count)
  const starts = new Uint32Array(count + 1)
  const points: number[] = []
  let x = 0
  let y = 0
  for (let way = 0; way < count; way++) {
    classes[way] = varint()
    starts[way] = points.length / 2
    const length = varint()
    for (let index = 0; index < length; index++) {
      x += zigzag()
      y += zigzag()
      points.push(x - halfWidth, y - halfHeight)
    }
  }
  starts[count] = points.length / 2
  return { classes, starts, points: Float32Array.from(points) }
}

/** The ways without the given classes. */
export function withoutClasses(ways: WorldWays, drop: readonly WayClass[]): WorldWays {
  const codes = new Set(drop.map((name) => WAY_CODE[name]))
  return waysFromLines(linesOf(ways).filter((line) => !codes.has(line.wayClass)))
}

/**
 * The ways trimmed to a budget: whole droppable classes go, service roads first, until `size` of their encoding fits
 * `budgetBytes`. `size` is how the caller counts (the baker counts gzipped bytes). Returns what was dropped.
 */
export function trimWays(
  ways: WorldWays,
  budgetBytes: number,
  size: (ways: WorldWays) => number,
): { ways: WorldWays; dropped: WayClass[] } {
  const dropped: WayClass[] = []
  let current = ways
  for (const name of DROPPABLE_CLASSES) {
    if (size(current) <= budgetBytes) break
    dropped.push(name)
    current = withoutClasses(ways, dropped)
  }
  return { ways: current, dropped }
}
